import { useEffect } from 'react';
import {
  AssistantChatProvider,
  useAssistantChatContext,
  useWorkspaceGetContext,
} from '@/features/assistant/components/assistant-chat-provider';
import { AssistantFullTab } from '@/features/assistant/components/assistant-full-tab';
import { useActiveEntityId } from '@/hooks/navigation';

/**
 * ConversationTab — renders ONE saved conversation as its own tab.
 *
 * Mounts an AssistantChatProvider keyed by the conversation id so this tab gets
 * its own Chat instance (via the connection-scoped registry). On mount it
 * hydrates the provider with the conversation's persisted messages. Switching to another
 * conversation tab mounts a DIFFERENT provider/Chat — no shared-state clobbering.
 *
 * Stale-ref constraint (from M9 review): the ConversationTab provider uses the
 * default getContext stub (activeEntityId: null, model: 'chat'). This means if
 * the sidebar also opens the SAME conversation id, they share one Chat/transport
 * via the registry, but the sidebar's WorkspaceAssistantChatProvider's getContext
 * won the factory race and is pinned in the ref. To avoid the stale-ref hazard
 * described in M9 review, we ensure only ONE provider per chatId is ever the
 * "primary" source of context callbacks — the root WorkspaceAssistantChatProvider
 * owns the workspace assistant overlay chat, and each ConversationTab owns its own
 * conversationId. Since conversation tabs use a unique conversationId (not
 * the overlay's stable WORKSPACE_CHAT_ID constant), there is no overlap. Two
 * ConversationTabs for the same conversation id are a degenerate case that
 * cannot arise from the current UI (history panel opens conversation in new tab,
 * which replaces any existing tab for that id via openTab dedup). If the
 * sidebar ever shows a conversation that also has a tab, they share one
 * Chat/transport — acceptable because both show the same conversation state.
 */
function ConversationLoader({ conversationId }: { conversationId: string }) {
  const { loadConversation } = useAssistantChatContext();
  useEffect(() => {
    void loadConversation(conversationId);
  }, [conversationId, loadConversation]);
  return null;
}

export function ConversationTab({
  conversationId,
  panelId,
}: {
  conversationId: string;
  panelId: string;
}) {
  const activeEntityId = useActiveEntityId();
  // M12 carry-forward (M10 review): pass the real getContext so model selection
  // and pinned-context forwarding work in per-tab chats — not just the overlay.
  const getContext = useWorkspaceGetContext();
  return (
    <AssistantChatProvider chatId={conversationId} getContext={getContext}>
      <ConversationLoader conversationId={conversationId} />
      <AssistantFullTab activeEntityId={activeEntityId ?? null} panelId={panelId} />
    </AssistantChatProvider>
  );
}
